import { useTranslation } from 'react-i18next'
import { Download, Trash2, CheckSquare, X } from 'lucide-react'

interface SelectionToolbarProps {
  selectedCount: number
  totalCount: number
  onClearSelection: () => void
  onSelectAll: () => void
  onBulkDownload?: () => void
  onBulkDelete?: () => void
}

export default function SelectionToolbar({
  selectedCount,
  totalCount,
  onClearSelection,
  onSelectAll,
  onBulkDownload,
  onBulkDelete
}: SelectionToolbarProps) {
  const { t } = useTranslation()

  const allSelected = selectedCount > 0 && selectedCount === totalCount

  // Detect platform for keyboard shortcut display
  const isMac = typeof navigator !== 'undefined' &&
    navigator.userAgent.toUpperCase().indexOf('MAC') >= 0
  const shortcutKey = isMac ? '⌘A' : 'Ctrl+A'

  return (
    <div className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border ${selectedCount > 0
      ? 'bg-primary/10 border-primary/30'
      : 'bg-base-200/50 border-base-300'
      }`}>

      <button
        className="btn btn-xs btn-ghost gap-1.5"
        onClick={onSelectAll}
        disabled={allSelected}
        title={t('fileExplorer.selection.selectAll')}
      >
        <CheckSquare className="w-4 h-4" />
        <span className="text-xs">{t('fileExplorer.selection.selectAll')}</span>
        <span className="text-xs opacity-60">({shortcutKey})</span>
      </button>

      {/* Selection counter - only visible when items are selected */}
      {selectedCount > 0 && (
        <>
          <span className="text-sm font-medium text-base-content/70 px-2">
            {t('fileExplorer.selection.selected', { count: selectedCount })}
          </span>
          <div className="h-4 w-px bg-base-300"></div>
        </>
      )}

      {/* Action buttons */}
      {selectedCount > 0 && (
        <div className="flex items-center gap-1">
          {onBulkDownload && (
            <button
              className="btn btn-xs btn-ghost btn-circle"
              onClick={onBulkDownload}
              title={t('fileExplorer.bulkActions.download')}
            >
              <Download className="w-4 h-4 text-secondary" />
            </button>
          )}
          {onBulkDelete && (
            <button
              className="btn btn-xs btn-ghost btn-circle"
              onClick={onBulkDelete}
              title={t('fileExplorer.bulkActions.delete')}
            >
              <Trash2 className="w-4 h-4 text-error" />
            </button>
          )}
          <button
            className="btn btn-xs btn-ghost btn-circle"
            onClick={onClearSelection}
            title={t('fileExplorer.selection.clearSelection')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  )
}
